import lscache from 'lscache'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'

const ONE_DAY = 24 * 60
const ONE_WEEK = ONE_DAY * 7

const isPreviewMode = () => {
	if (!ENV_DEV_OR_STAGING) return false
	const urlParams = new URLSearchParams(window.location.search)
	return urlParams.has('is_ob_preview')
}

const createMemoryStorage = () => {
	const storage = {}
	return {
		getItem: (key) => storage[key] ?? null,
		setItem: (key, value) => {
			storage[key] = value
		},
		removeItem: (key) => {
			delete storage[key]
		},
	}
}

const createLscacheStorage = (cacheTime) => {
	if (isPreviewMode()) {
		return createMemoryStorage()
	}
	return {
		getItem: (key) => {
			const val = lscache.get(key)
			if (val == null) return null
			return typeof val === 'string' ? val : JSON.stringify(val)
		},
		setItem: (key, value) => {
			// NOTE: pinia-plugin-persistedstate calls setItem with already-serialized JSON.
			// lscache.set() does JSON.stringify internally → double-encoded JSON in localStorage.
			// This is INTENTIONAL for backward compat with old vuex-persistedstate behavior.
			// FUTURE FIX: custom lscache wrapper skipping internal stringify.
			lscache.set(key, value, cacheTime)
		},
		removeItem: (key) => lscache.remove(key),
	}
}

export const weeklyStorage = createLscacheStorage(ONE_WEEK)
export const dailyStorage = createLscacheStorage(ONE_DAY)
export const permanentStorage = createLscacheStorage()
export const sessionOrPreviewStorage = isPreviewMode() ? createMemoryStorage() : window.sessionStorage

/**
 * Read the current blob from a shared storage key.
 */
function readBlob(storage, key) {
	try {
		const raw = storage.getItem(key)
		if (raw) return JSON.parse(raw)
	} catch {
		/* ignore */
	}
	return {}
}

/**
 * Creates a pinia-plugin-persistedstate config that shares a single storage key
 * across multiple Pinia stores — preserving backward compatibility with the old
 * Vuex vuex-persistedstate layout where one key held a flat object of namespaces:
 *   { survey: {...}, location: {...}, userFlags: {...}, ... }
 *
 * @param {string} sharedKey   – the storage key (e.g. 'vuex', 'onedaystorage', 'auth')
 * @param {string} namespace   – property name inside the blob (e.g. 'survey')
 * @param {object} storage     – storage adapter (weeklyStorage / dailyStorage / …)
 * @param {string[]} [paths]   – optional paths to persist (subset of state)
 */
export function sharedKeyPersist(sharedKey, namespace, storage, paths) {
	return {
		key: sharedKey,
		storage,
		...(paths ? { paths } : {}),
		serializer: {
			serialize: (state) => {
				const existing = readBlob(storage, sharedKey)
				existing[namespace] = state
				return JSON.stringify(existing)
			},
			deserialize: (raw) => {
				try {
					const blob = JSON.parse(raw)
					return blob[namespace] ?? {}
				} catch {
					return {}
				}
			},
		},
	}
}

/**
 * Like sharedKeyPersist but spreads state keys at the ROOT level of the blob
 * instead of nesting under a namespace. Used for stores whose state properties
 * lived at the top level of the old Vuex blob (e.g. absmartly, email, auth).
 *
 * @param {string} sharedKey – the storage key
 * @param {object} storage   – storage adapter
 * @param {string[]} paths   – required: which state keys to persist
 */
export function sharedKeyPersistFlat(sharedKey, storage, paths) {
	return {
		key: sharedKey,
		storage,
		paths,
		serializer: {
			serialize: (state) => {
				const existing = readBlob(storage, sharedKey)
				Object.assign(existing, state)
				return JSON.stringify(existing)
			},
			deserialize: (raw) => {
				try {
					const blob = JSON.parse(raw)
					const result = {}
					for (const p of paths) {
						if (p in blob) result[p] = blob[p]
					}
					return result
				} catch {
					return {}
				}
			},
		},
	}
}
